import React, { useRef, useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import axiosInstance from "../../utils/axiosInstance";
import { customerQueriesUrl, customerQueryByIdUrl, customerQueryStatusUrl } from "../../utils/urls";
import { toast } from "react-hot-toast";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  Box,
  Stack,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Grid2,
  CircularProgress,
} from "@mui/material";
import PageHeader from "../../components/page-layout/PageHeader";
import {
  Email,
  WhatsApp,
  MoreHoriz,
  Edit,
  Archive,
  FileCopy,
  CheckCircle,
  NotInterested,
  Update,
} from "@mui/icons-material";
import { GridCheckCircleIcon } from "@mui/x-data-grid";

// Generate mock query
// const generateQuery = (id) => ({
//   id,
//   customerName: `Customer ${id}`,
//   date: `2024-07-${String((id % 30) + 1).padStart(2, "0")}`,
//   storeName: `Xerox Cloud - Store ${id % 5}`,
//   type: ["Feedback", "Bug", "Report", "Contact"][id % 4],
//   contact: `90000000${String(id).padStart(2, "0")}`,
//   email: `customer${id}@example.com`,
//   message: `Sample message from customer ${id}. This is a test query.`,
//   selectedOption: null,
// });

const Queries = () => {
  // ...existing state

  // Fetch queries from API
  const fetchQueries = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(customerQueriesUrl + `?page=${pageNumber}`);
      const data = res.data;
      // Use API meta fields
      setQueries(prev => pageNumber === 1 ? data.data : [...prev, ...data.data]);
      setTotalPages(data.meta?.totalPages || 1);
      setHasMore(pageNumber < (data.meta?.totalPages || 1));
    } catch (err) {
      toast.error("Failed to load customer queries");
    } finally {
      setLoading(false);
    }
  };

  // Load first page on mount
  useEffect(() => {
    fetchQueries(1);
    setPage(1);
  }, []);

  // Infinite scroll handler
  const fetchMoreData = () => {
    if (page < totalPages) {
      const nextPage = page + 1;
      fetchQueries(nextPage);
      setPage(nextPage);
    } else {
      setHasMore(false);
    }
  };
  // Remove any old fetchMoreData definitions below this line if present.
  const [open, setOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [queries, setQueries] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleOpen = (query) => {
    setSelectedQuery(query);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleOptionSelect = async (index, option) => {
    const query = queries[index];
    const status = option; // option is status string (e.g., 'PENDING', 'RESOLVED', 'REJECTED')
    handleMenuClose();
    
    // Optimistically update the UI
    const updatedQueries = [...queries];
    updatedQueries[index] = {
      ...updatedQueries[index],
      status: status
    };
    setQueries(updatedQueries);
    
    try {
      await axiosInstance.patch(customerQueryStatusUrl(query.id, status));
    } catch (err) {
      // Revert on error
      const revertedQueries = [...queries];
      revertedQueries[index] = {
        ...revertedQueries[index],
        status: query.status // Revert to original status
      };
      setQueries(revertedQueries);
      toast.error('Failed to update status');
    }
  };

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Customer Queries"
        lead="All types of queries are displayed here"
        showBack={true}
      />

      <Box
        id="scrollableDiv"
        sx={{
          height: 'calc(100vh - 200px)',
          overflow: 'auto',
          p: 2
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          <InfiniteScroll
            dataLength={queries.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
          >
            <Grid2 container spacing={2} sx={{ width: "100%" }}>
              {queries.map((query, index) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={query.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      boxShadow: "none",
                      minHeight: 130,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      bgcolor: "background.paper",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <CardContent sx={{ position: "relative" }}>
                      <Typography fontSize={14} fontWeight={600}>
                        {query.customer_details?.name || "Customer"}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {new Date(query.createdAt).toLocaleString()}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {query.storeName}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          mt: 1,
                          mb: 1,
                          position: "absolute",
                          top: 0,
                          right: 8,
                        }}
                      >
                        <Chip
                          label={query?.status ? query.status.charAt(0).toUpperCase() + query.status.slice(1).toLowerCase() : ''}
                          color={
                            query?.status === 'PENDING' ? 'secondary' :
                              query?.status === 'RESOLVED' ? 'success' :
                                query?.status === 'ASSIGNED' ? 'info' :
                                  'default'
                          }
                          variant="contained"
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Chip
                          label={query.queryType}
                          color="warning"
                          variant="contained"
                          size="small"
                        />
                      </Stack>

                      <Typography
                        fontSize={12}
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={query.message}
                      >
                        {query.message}
                      </Typography>
                    </CardContent>
                    <Box
                      sx={{
                        pb: 2,
                        pl: 2,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpen(query)}
                      >
                        View Message
                      </Button>
                      <Box>
                        <Button
                          size="small"
                          onClick={(event) => handleMenuClick(event, index)}
                          aria-controls={
                            Boolean(anchorEl) && menuIndex === index
                              ? "card-options-menu"
                              : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            Boolean(anchorEl) && menuIndex === index
                              ? "true"
                              : undefined
                          }
                        >
                          <MoreHoriz />
                        </Button>
                        <Menu
                          id="card-options-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && menuIndex === index}
                          onClose={handleMenuClose}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                          <MenuItem
                            onClick={() => handleOptionSelect(index, "PENDING")}
                          >
                            <Update fontSize="small" sx={{ mr: 1 }} /> Pending
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleOptionSelect(index, "ASSIGNED")}
                          >
                            <NotInterested fontSize="small" sx={{ mr: 1 }} /> Assigned
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleOptionSelect(index, "RESOLVED")}
                          >
                            <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Resolved
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Box>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </InfiniteScroll>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 2px 16px 0 rgba(60,72,100,0.10)",
            bgcolor: "background.paper",
          },
        }}
      >
        <Box sx={{ p: 2, pb: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontSize={14} fontWeight={600}>
              {selectedQuery?.customer_details?.name || "Customer"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={selectedQuery?.status ? selectedQuery.status.charAt(0).toUpperCase() + selectedQuery.status.slice(1).toLowerCase() : ''}
                color={
                  selectedQuery?.status === 'PENDING' ? 'secondary' :
                    selectedQuery?.status === 'RESOLVED' ? 'success' :
                      selectedQuery?.status === 'ASSIGNED' ? 'info' :
                        'default'
                }
                variant="contained"
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
              <Chip
                label={selectedQuery?.queryType ? selectedQuery.queryType.charAt(0).toUpperCase() + selectedQuery.queryType.slice(1).toLowerCase() : 'No Type'}
                color="warning"
                variant="contained"
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          </Stack>
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 0.5 }}>
            {selectedQuery?.storeName}
          </Typography>
          <Typography fontSize={12} color="text.secondary" sx={{ mb: 2 }}>
            {selectedQuery?.createdAt && new Date(selectedQuery.createdAt).toLocaleString()}
          </Typography>
          <Typography
            sx={{
              p: 2,
              mb: 2,
              color: "text.primary",
              fontSize: "1rem",
              whiteSpace: "pre-line",
            }}
          >
            {selectedQuery?.message}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            bgcolor: "background.paper",
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <MailButton email={selectedQuery?.customer_details?.email} query={selectedQuery} />
          <Button
            variant="outlined"
            color="success"
            startIcon={<WhatsApp />}
            onClick={() => {
              if (selectedQuery?.contact) {
                window.open(
                  `https://wa.me/91${selectedQuery.contact}?text=Hi%20${encodeURIComponent(
                    selectedQuery.customerName || selectedQuery.userId 
                  )},%20regarding%20your%20query%20at%20${encodeURIComponent(
                    selectedQuery.storeName || selectedQuery.storeId
                  )}.`
                );
              }
            }}
          >
            WhatsApp
          </Button>
        </Box>
      </Dialog>
    </Stack>
  );
};

export default Queries;

const MailButton = ({ email, query }) => {
  const anchorRef = useRef(null);

  const handleClick = () => {
    anchorRef.current?.click();
  };

  if (!email) return null;

  const subject = encodeURIComponent("Reply to your query");
  let body = `Hi ${email},\n\n`;

  switch (query.type) {
    case "Feedback":
      body += "Thank you for your valuable feedback.\n\n";
      break;
    case "Bug":
      body += "Thank you for reporting this issue.\n\n";
      break;
    case "Report":
      body += "Thank you for your report.\n\n";
      break;
    case "Contact":
      body += "Thank you for reaching out.\n\n";
      break;
    default:
      body += "Thank you for contacting us.\n\n";
  }

  body += `Your message: "${query.message}" has reached us. We are working on it and will address your query soon.\n\nBest regards,\nXerox Cloud Team`;

  const mailto = `mailto:${email}?subject=${subject}&body=${encodeURIComponent(body)}`;

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<Email />}
        onClick={handleClick}
      >
        Email
      </Button>
      <a href={mailto} ref={anchorRef} style={{ display: "none" }}>
        Hidden Mail Link
      </a>
    </>
  );
};
